import {textToHtml} from '@frontapp/editor-html';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {DropdownBoxCoordinator} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dropdown, DropdownProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {autoGenerateDraft, DraftAssistantToneOptionsEnum} from '../../../../../helpers/draftAssistantHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {DraftMessageModel, TimelineMessageModel} from '../../../../../models/api/messageModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {HttpResponseError} from '../../../../../services/http/httpError';
import {DialogButtonGroup} from '../../../../common/dialogs/dialogButtonGroup';
import {DialogContent} from '../../../../common/dialogs/dialogContent';
import {DraftAssistantLoader} from './draftAssistantLoader';

/*
 * Types.
 */

const toneOptions = [
  DraftAssistantToneOptionsEnum.PROFESSIONAL,
  DraftAssistantToneOptionsEnum.FRIENDLY,
  DraftAssistantToneOptionsEnum.APOLOGETIC,
  DraftAssistantToneOptionsEnum.FUNNY,
];

/*
 * Props.
 */

interface DraftAssistantDropdownProps extends DropdownProps {
  onDraftAssistantInsertDraft: HandlerOf<string>;
  conversation: ConversationModel | null;
  draftMessage: DraftMessageModel;
  previousMessage: TimelineMessageModel | null;
  author: TeammateModel | undefined;
  companyUrl: string;
}

/*
 * Constants.
 */

const configurationDropdownWidth = 320;

/*
 * Styles.
 */

const StyledTextDiv = styled.div`
  padding: 12px;
`;

const StyledDescriptionDiv = styled.div`
  padding-bottom: 12px;
`;

/*
 * Component.
 */

export const DraftAssistantDropdown: FC<DraftAssistantDropdownProps> = (props) => {
  const {author, companyUrl, conversation, draftMessage, previousMessage, ...dropdownProps} = props;

  const [tone, setTone] = useState<DraftAssistantToneOptionsEnum>(DraftAssistantToneOptionsEnum.PROFESSIONAL);
  const [loadingStatus, setLoadingStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADED);
  const [error, setError] = useState<HttpResponseError | undefined>(undefined);

  // Disabled state: disable AI compose if we don't have any context to compose from (no replying message and empty draft)
  const draftIsEmpty = draftMessage.blurb.length === 0;
  if (!conversation || (draftIsEmpty && !previousMessage)) {
    return (
      <Dropdown {...dropdownProps} width={configurationDropdownWidth}>
        <DropdownHeader title={<Trans id="draft-assistant-magiccompose">Compose with AI</Trans>} />
        <StyledTextDiv>
          <Trans id="draft-assistant-autocompose-start-draft">
            Start a draft to unlock AI compose options.
          </Trans>
        </StyledTextDiv>
      </Dropdown>
    );
  }

  const onClickSave = async () => {
    setLoadingStatus(AsyncStatusesEnum.LOADING);

    const draft = await autoGenerateDraft({
      tone,
      draftMessage,
      senderFirstName: author ? author.givenName : null,
      companyUrl,
      conversationId: conversation.id,
    }).catch((err) => {
      setLoadingStatus(AsyncStatusesEnum.FAILED);
      if (err instanceof HttpResponseError) {
        setError(err);
      }
    });

    // Something went wrong - set loading state to failed so user can retry.
    if (!draft) {
      setLoadingStatus(AsyncStatusesEnum.FAILED);
      return;
    }

    props.onDraftAssistantInsertDraft(textToHtml(draft));
    setLoadingStatus(AsyncStatusesEnum.LOADED);
    setError(undefined);

    if (dropdownProps.onRequestClose) {
      dropdownProps.onRequestClose();
    }
  };

  const renderDropdownContent = () => {
    // Loading and error states
    if (loadingStatus === AsyncStatusesEnum.LOADING || loadingStatus === AsyncStatusesEnum.FAILED) {
      return (
        <DraftAssistantLoader
          error={error}
          loadingStatus={loadingStatus}
          loadingMessage={
            <Trans id="draft-assistant-autocompose-async-loading">Generating your draft...</Trans>
          }
          onRetryClick={onClickSave}
        />
      );
    }

    return (
      <>
        <DialogContent>
          <StyledDescriptionDiv>
            <Trans id="draft-assistant-autocompose-tip">
              A draft will be generated based on your current draft and the message you’re replying to. Try
              providing short bullet points to generate more accurate drafts.
            </Trans>
          </StyledDescriptionDiv>
          <DropdownBoxCoordinator
            label={<Trans id="draft-assistant-autocompose-tone">Tone</Trans>}
            value={tone}
            renderDropdown={({anchorWidth, anchor, onRequestClose}) => (
              <SimpleDropdown width={anchorWidth} anchor={anchor} onRequestClose={onRequestClose}>
                {toneOptions.map((option) => (
                  <SimpleDropdownItem key={option} onSelect={() => setTone(option)}>
                    {option}
                  </SimpleDropdownItem>
                ))}
              </SimpleDropdown>
            )}
          />
        </DialogContent>
        <DialogButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={dropdownProps.onRequestClose}>
            <Trans id="dropdown-cancel">Cancel</Trans>
          </Button>
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onClickSave}>
            <Trans id="draft-assistant-generate-button">Generate</Trans>
          </Button>
        </DialogButtonGroup>
      </>
    );
  };

  return (
    <Dropdown {...dropdownProps} width={configurationDropdownWidth} placement="top-start">
      <DropdownHeader title={<Trans id="draft-assistant-magiccompose">Compose with AI</Trans>} />
      {renderDropdownContent()}
    </Dropdown>
  );
};
